import { closeInAppBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { Platform } from "@src/mobile/utils/platform";
import { Hub } from "aws-amplify/utils";
import { type ReactElement, useEffect, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk in SSO callback handler
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { fetchAuthSessionWithLock } from "../cognito/cognitoSessionLock";
import { initCognitoSession } from "../cognito/initCognitoSession";
import { isSafeRedirectUrl } from "../cognito/isSafeRedirectUrl";
import {
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "../loginRum";
import {
  cleanupSsoState,
  exchangeCodeOnCapacitor,
  hasStoredCognitoSession,
} from "./capacitorSsoHelpers";
import { RipplingSsoCallbackView } from "./RipplingSsoCallbackView";
import {
  clearStaleSessionKeys,
  isAccountLinkedError,
  isIdpInitiatedFlow,
} from "./ssoCallbackHelpers";
import {
  RIPPLING_SSO_REQUESTED_URL_KEY,
  SSO_PR_PREVIEW_KEY,
  startSsoRedirect,
} from "./startSsoRedirect";

const DEFAULT_REDIRECT_PATH = "/";
const SSO_CALLBACK_TIMEOUT_MS = 30_000;

export function RipplingSsoCallback(): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const [error, setError] = useState<string | undefined>();
  const hasHandled = useRef(false);

  // Capture URL params on first render, before the URL-stripping useEffect
  // removes them. On Capacitor the deep link arrives with ?code=...&state=...
  // and a later useEffect calls history.replaceState to strip the params.
  const callbackParams = useRef(new URLSearchParams(globalThis.location.search));

  // Handle two cases that require silently re-initiating the SSO flow:
  //
  // 1. SSO_ACCOUNT_LINKED: First SSO login for employees with an existing
  //    Cognito account. The PreSignUp Lambda links the Rippling identity and
  //    throws. The second attempt authenticates via the linked native user.
  //
  // 2. IdP-initiated flow: User clicked the Rippling tile directly instead
  //    of the app's SSO button. Amplify has no stored OAuth state, so it
  //    can't exchange the auth code. Re-initiating from the frontend gives
  //    Amplify a proper OAuth flow with code challenge.
  const needsRetry = isAccountLinkedError() || isIdpInitiatedFlow();

  // Limit retries to prevent infinite redirect loops if the SSO_ACCOUNT_LINKED
  // error or IdP-initiated detection keeps triggering on every callback.
  const SSO_RETRY_KEY = "sso_retry_count";
  const MAX_SSO_RETRIES = 1;
  const retryCount = Number(sessionStorage.getItem(SSO_RETRY_KEY) ?? "0");
  const shouldRetryFlow = needsRetry && retryCount < MAX_SSO_RETRIES;

  useEffect(() => {
    // Set first so retry-path RUM events carry the `identityProvider` tag.
    setLoginIdentityProvider(IDENTITY_PROVIDER.RIPPLING_SSO);

    if (needsRetry && retryCount >= MAX_SSO_RETRIES) {
      logError(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_RETRY_EXHAUSTED, {
        error: new Error("SSO callback retry exhausted"),
        metadata: {
          reason: isAccountLinkedError()
            ? LOGIN_REASON.ACCOUNT_LINKED_RETRY_EXHAUSTED
            : LOGIN_REASON.IDP_INITIATED_RETRY_EXHAUSTED,
          outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
          retryCount,
        },
      });
      setError("Sign in with Rippling failed. Please try again.");
      sessionStorage.removeItem(SSO_RETRY_KEY);
      return;
    }

    if (shouldRetryFlow) {
      const retryReason = isAccountLinkedError() ? "account_linked" : "idp_initiated";
      logEvent(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_RETRY, {
        reason: retryReason,
        retryCount: retryCount + 1,
      });
      sessionStorage.setItem(SSO_RETRY_KEY, String(retryCount + 1));
      // Use startSsoRedirect for retries — on Capacitor it opens the in-app
      // browser instead of calling signInWithRedirect (which can't navigate).
      startSsoRedirect().catch((retryError: unknown) => {
        logError(APP_V2_APP_EVENTS.RIPPLING_SSO_REDIRECT_FAILURE, {
          error: retryError,
          metadata: {
            outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
            reason: isNetworkFailure(retryError)
              ? LOGIN_REASON.NETWORK_FAILURE
              : LOGIN_REASON.SSO_REDIRECT_FAILED,
          },
        });
        sessionStorage.removeItem(SSO_RETRY_KEY);
        setError("Sign in with Rippling failed. Please try again.");
      });
    }
  }, [shouldRetryFlow, needsRetry, retryCount]);

  // Strip authorization code and state from URL to prevent exposure
  // in browser history or referrer headers.
  useEffect(() => {
    if (!shouldRetryFlow) {
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    }
  }, [shouldRetryFlow]);

  useEffect(() => {
    // Skip the normal callback flow when re-initiating (account linking or IdP-initiated).
    if (shouldRetryFlow) {
      return;
    }

    let cancelled = false;

    function redirectToTarget(): void {
      const requestedUrl = localStorage.getItem(RIPPLING_SSO_REQUESTED_URL_KEY);
      localStorage.removeItem(RIPPLING_SSO_REQUESTED_URL_KEY);
      localStorage.removeItem(SSO_PR_PREVIEW_KEY);
      const target =
        requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;
      history.replace(target);
    }

    function logCallbackSuccess(): void {
      logEvent(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_SUCCESS, {
        outcome: LOGIN_OUTCOME.SUCCESS,
      });
    }

    async function completeSignIn() {
      if (hasHandled.current) {
        return;
      }
      hasHandled.current = true;

      // Recovery path for the Capacitor remount race — see `handleFailure`.
      if (Platform.isMobile && hasStoredCognitoSession()) {
        sessionStorage.removeItem(SSO_RETRY_KEY);
        logCallbackSuccess();
        redirectToTarget();
        return;
      }

      clearStaleSessionKeys();

      try {
        // On Capacitor, exchange the auth code for tokens manually since
        // Amplify's enable-oauth-listener can't detect the callback (the
        // deep link arrives via SPA navigation, not a page load at the
        // configured redirectSignIn origin).
        if (Platform.isMobile) {
          const authCode = callbackParams.current.get("code");
          if (!authCode) {
            throw new Error("No authorization code in callback URL");
          }
          await exchangeCodeOnCapacitor(authCode);
        }

        // Hydrate the Redux session before navigating so PrivateRoute
        // sees a valid session immediately — same pattern as AuthRedirect.
        // initCognitoSession deduplicates concurrent calls, so if AppLayout's
        // useEffect also calls it, the second call returns the same promise.
        const sessionRestored = await dispatch(initCognitoSession());

        if (!sessionRestored) {
          throw new Error("Failed to restore session after SSO sign-in");
        }

        sessionStorage.removeItem(SSO_RETRY_KEY);
        logCallbackSuccess();

        // Dismiss the in-app browser that was opened for the OAuth flow.
        // Adapter swallows the Android auto-dismiss "No active browser instance"
        // error — letting it bubble would hit our catch block and call
        // `cleanupSsoState()`, wiping the tokens we just stored.
        if (Platform.isMobile) {
          await closeInAppBrowser();
        }

        if (!cancelled) {
          redirectToTarget();
        }
      } catch (signInError: unknown) {
        logError(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_FAILURE, {
          error: signInError,
          metadata: {
            outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
            reason: isNetworkFailure(signInError)
              ? LOGIN_REASON.NETWORK_FAILURE
              : LOGIN_REASON.SSO_CALLBACK_FAILED,
          },
        });
        cleanupSsoState();
        if (!cancelled) {
          setError("Sign in with Rippling failed. Please try again.");
        }
      }
    }

    function handleFailure(errorMessage?: string) {
      if (cancelled || hasHandled.current) {
        return;
      }
      // Recovery path: a parent re-render unmounted us between
      // `exchangeCodeOnCapacitor` and `history.replace(target)`. Tokens are
      // already stored — redirect instead of wiping a valid session.
      if (Platform.isMobile && hasStoredCognitoSession()) {
        hasHandled.current = true;
        sessionStorage.removeItem(SSO_RETRY_KEY);
        logCallbackSuccess();
        redirectToTarget();
        return;
      }
      hasHandled.current = true;
      cleanupSsoState();
      logError(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_FAILURE, {
        error: new Error(errorMessage ?? "SSO sign-in failed"),
        metadata: {
          outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
          reason: LOGIN_REASON.SSO_CALLBACK_FAILED,
        },
      });
      setError("Sign in with Rippling failed. Please try again.");
    }

    // On Capacitor, we handle the token exchange ourselves — skip the Hub
    // listener and go straight to completeSignIn.
    if (Platform.isMobile) {
      if (callbackParams.current.has("code")) {
        void completeSignIn();
      } else {
        handleFailure("No authorization code in callback URL");
      }

      const timeout = setTimeout(() => {
        handleFailure("SSO callback timed out");
      }, SSO_CALLBACK_TIMEOUT_MS);

      return () => {
        cancelled = true;
        clearTimeout(timeout);
      };
    }

    // Web path: Amplify's enable-oauth-listener exchanges the code and
    // fires a Hub event when done.
    const unsubscribe = Hub.listen("auth", (data) => {
      if (data.payload.event === "signInWithRedirect") {
        void completeSignIn();
      }
      if (data.payload.event === "signInWithRedirect_failure") {
        handleFailure("signInWithRedirect_failure event received");
      }
    });

    // The Hub event may have already fired before our listener attached.
    // Check if a valid session already exists.
    void (async () => {
      try {
        const session = await fetchAuthSessionWithLock();
        if (session.tokens?.idToken && !hasHandled.current) {
          void completeSignIn();
        }
      } catch {
        // No existing session — wait for Hub event
      }
    })();

    const timeout = setTimeout(() => {
      handleFailure("SSO callback timed out");
    }, SSO_CALLBACK_TIMEOUT_MS);

    return () => {
      cancelled = true;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [dispatch, history, shouldRetryFlow]);

  return <RipplingSsoCallbackView error={error} />;
}
